import moment from "moment-timezone";

/**
 * Centralized Timezone Utilities
 * Provides consistent timezone conversion logic throughout the calendar component
 */

/**
 * Get the application timezone
 * @param {string} fallbackTz - Fallback timezone if global config is not available
 * @returns {string} The application timezone
 */
export const getAppTimezone = (fallbackTz = 'UTC') => {
    if (typeof global !== 'undefined' && global.config && global.config.getTimeZone) {
        return global.config.getTimeZone() || fallbackTz || 'Asia/Kolkata';
    }
    return fallbackTz || 'UTC';
};

/**
 * Convert a date to the application timezone
 * @param {Date|string|moment.Moment} date - The date to convert
 * @param {string} tz - The target timezone (defaults to app timezone)
 * @param {boolean} isAllDay - Whether this is an all-day event (will set to start of day)
 * @returns {moment.Moment} A moment object in the target timezone
 */
export const toAppTimezone = (date, tz = null, isAllDay = false) => {
    const appTz = tz || getAppTimezone();
    let momentDate = moment.tz(date, appTz);
    
    if (isAllDay) {
        momentDate = momentDate.startOf('day');
    }
    
    return momentDate;
};

/**
 * Convert a date to the application timezone and return as Date object
 * @param {Date|string|moment.Moment} date - The date to convert
 * @param {string} tz - The target timezone (defaults to app timezone)
 * @param {boolean} isAllDay - Whether this is an all-day event (will set to start of day)
 * @returns {Date} A Date object representing the time in the target timezone
 */
export const toAppTimezoneDate = (date, tz = null, isAllDay = false) => {
    return toAppTimezone(date, tz, isAllDay).toDate();
};

/**
 * Convert a UTC date to app timezone, handling UTC timezone strings
 * @param {Date|string|moment.Moment} date - The date to convert
 * @param {string} sourceTimezone - The source timezone (if 'UTC', will use UTC conversion)
 * @param {string} targetTz - The target timezone (defaults to app timezone)
 * @param {boolean} isAllDay - Whether this is an all-day event
 * @returns {moment.Moment} A moment object in the target timezone
 */
export const convertTimezone = (date, sourceTimezone, targetTz = null, isAllDay = false) => {
    const appTz = targetTz || getAppTimezone();
    
    // Handle UTC timezone explicitly
    if (sourceTimezone === 'UTC') {
        const momentDate = moment.utc(date);
        if (isAllDay) {
            return momentDate.tz(appTz).startOf('day');
        }
        return momentDate.tz(appTz);
    }
    
    // Use the source timezone if provided, otherwise assume app timezone
    const sourceTz = sourceTimezone || appTz;
    return toAppTimezone(date, sourceTz, isAllDay);
};

/**
 * Convert a UTC date to app timezone and return as Date object
 * @param {Date|string|moment.Moment} date - The date to convert
 * @param {string} sourceTimezone - The source timezone (if 'UTC', will use UTC conversion)
 * @param {string} targetTz - The target timezone (defaults to app timezone)
 * @param {boolean} isAllDay - Whether this is an all-day event
 * @returns {Date} A Date object representing the time in the target timezone
 */
export const convertTimezoneDate = (date, sourceTimezone, targetTz = null, isAllDay = false) => {
    return convertTimezone(date, sourceTimezone, targetTz, isAllDay).toDate();
};

/**
 * Format a date in the application timezone
 * @param {Date|string|moment.Moment} date - The date to format
 * @param {string} format - The format string (defaults to 'YYYY-MM-DD')
 * @param {string} tz - The timezone (defaults to app timezone)
 * @returns {string} Formatted date string
 */
export const formatInTimezone = (date, format = 'YYYY-MM-DD', tz = null) => {
    const appTz = tz || getAppTimezone();
    return moment.tz(date, appTz).format(format);
};

/**
 * Set time on a date in the application timezone
 * @param {Date|string|moment.Moment} date - The base date
 * @param {number} hour - The hour (0-23)
 * @param {number} minute - The minute (0-59)
 * @param {number} second - The second (0-59), defaults to 0
 * @param {string} tz - The timezone (defaults to app timezone)
 * @returns {Date} A Date object with the time set
 */
export const setTimeInTimezone = (date, hour, minute, second = 0, tz = null) => {
    const appTz = tz || getAppTimezone();
    return moment.tz(date, appTz)
        .set({ hour, minute, second })
        .toDate();
};

/**
 * Get start of day in the application timezone
 * @param {Date|string|moment.Moment} date - The date
 * @param {string} tz - The timezone (defaults to app timezone)
 * @returns {Date} A Date object representing start of day
 */
export const startOfDayInTimezone = (date, tz = null) => {
    const appTz = tz || getAppTimezone();
    return moment.tz(date, appTz).startOf('day').toDate();
};

/**
 * Get end of day in the application timezone
 * @param {Date|string|moment.Moment} date - The date
 * @param {string} tz - The timezone (defaults to app timezone)
 * @returns {Date} A Date object representing end of day
 */
export const endOfDayInTimezone = (date, tz = null) => {
    const appTz = tz || getAppTimezone();
    return moment.tz(date, appTz).endOf('day').toDate();
};

/**
 * Add days to a date in the application timezone
 * @param {Date|string|moment.Moment} date - The base date
 * @param {number} days - Number of days to add (can be negative)
 * @param {string} tz - The timezone (defaults to app timezone)
 * @returns {Date} A Date object with days added
 */
export const addDaysInTimezone = (date, days, tz = null) => {
    const appTz = tz || getAppTimezone();
    return moment.tz(date, appTz).add(days, 'days').toDate();
};

